import React, { useState } from 'react';
import { useAdmin } from '../context/AdminContext';
import { useNavigate } from 'react-router-dom';
import AddProductModal from '../components/AddProductModal';
import EditProductModal from '../components/EditProductModal';
import Sidebar from '../components/Sidebar';
import { adminFetchInit } from '../utils/adminToken';

// This will be replaced with API data

const AdminStore = () => {
  const { isAuthenticated, isLoading } = useAdmin();
  const navigate = useNavigate();
  
  // State for sidebar and UI controls
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedVendor, setSelectedVendor] = useState('all');
  const [viewMode, setViewMode] = useState('table');
  
  // State for products data
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [productsError, setProductsError] = useState(null);
  const [isFallbackData, setIsFallbackData] = useState(false);
  
  // State for add product modal
  const [showAddProductModal, setShowAddProductModal] = useState(false);
  
  // State for edit product modal
  const [showEditProductModal, setShowEditProductModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  
  // State for delete confirmation
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingProduct, setDeletingProduct] = useState(null);
  
  // State for toast notifications
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success'); // 'success' or 'error'
  
  // State for product update loading
  const [isUpdatingProducts, setIsUpdatingProducts] = useState(false);
  
  // Redirect if not authenticated
  React.useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/admin/login');
    }
  }, [isAuthenticated, isLoading, navigate]);

  // Fetch products from Shopify API
  const fetchProducts = async () => {
    try {
      setLoadingProducts(true);
      setProductsError(null);
      
      console.log('🛍️ Fetching products from Shopify API...');

      const response = await fetch('/.netlify/functions/shopify-products', adminFetchInit());

      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        const msg = errBody.message || `HTTP error! status: ${response.status}`;
        throw new Error(msg);
      }
      
      const data = await response.json();
      
      if (data.success) {
        setProducts(data.products || []);
        setIsFallbackData(data.fallback || false);
        
        if (data.fallback) {
          console.log('⚠️ Using fallback data:', data.message);
        } else {
          console.log('✅ Shopify products loaded:', data.products.length);
        }
      } else {
        throw new Error(data.message || 'Failed to fetch products');
      }
    } catch (error) {
      console.error('❌ Error fetching products:', error);
      setProductsError(error.message);
      // Set empty array on error
      setProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  };

  // Load products on component mount
  React.useEffect(() => {
    if (isAuthenticated) {
      fetchProducts();
    }
  }, [isAuthenticated]);

  // Filter and sort products based on search, filters, and date (newest first)
  const filteredProducts = products
    .filter(product => {
      const matchesSearch = (product.name ?? '').toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = selectedCategory === 'all' || product.category === selectedCategory;
      const matchesStatus = selectedStatus === 'all' || product.status === selectedStatus;
      const matchesVendor = selectedVendor === 'all' || product.vendor === selectedVendor;
      
      return matchesSearch && matchesCategory && matchesStatus && matchesVendor;
    })
    .sort((a, b) => {
      // Sort by creation date in descending order (newest first)
      const dateA = new Date(a.created_at || a.createdAt || a.dateAdded || 0);
      const dateB = new Date(b.created_at || b.createdAt || b.dateAdded || 0);
      return dateB - dateA;
    });

  // Get unique categories and vendors for filter dropdowns
  const categories = [...new Set(products.map(product => product.category))];
  const vendors = [...new Set(products.map(product => product.vendor).filter(vendor => vendor))];



  // Toggle mobile menu
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Handle add product modal
  const openAddProductModal = () => {
    setShowAddProductModal(true);
  };

  const closeAddProductModal = () => {
    setShowAddProductModal(false);
  };

  // Handle edit product modal
  const openEditProductModal = (product) => {
    setEditingProduct(product);
    setShowEditProductModal(true);
  };

  const closeEditProductModal = () => {
    setShowEditProductModal(false);
    setEditingProduct(null);
  };

  // Handle delete confirmation
  const openDeleteConfirm = (product) => {
    setDeletingProduct(product);
    setShowDeleteConfirm(true);
  };

  const closeDeleteConfirm = () => {
    setShowDeleteConfirm(false);
    setDeletingProduct(null);
  };

  // Toast function
  const showToastMessage = (message, type = 'success') => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
    
    setTimeout(() => {
      setShowToast(false);
    }, 4000);
  };

  // Handle product added successfully
  const handleProductAdded = (newProduct) => {
    // Add the new product to the beginning of the products list
    setProducts(prev => [newProduct, ...prev]);
    
    // Show success toast
    showToastMessage(`Product "${newProduct.name}" created successfully!`, 'success');
    
    console.log('✅ Product added successfully:', newProduct);
    
    // Optionally refresh the products list to ensure sync
    fetchProducts();
  };

  // Handle product updated successfully
  const handleProductUpdated = (updatedProduct) => {
    // Immediately update local state to show changes instantly
    setProducts(prev => 
      prev.map(product => 
        product.shopifyId === updatedProduct.shopifyId || product.id === updatedProduct.id
          ? updatedProduct 
          : product
      )
    );
    
    // Show success toast
    showToastMessage(`Product "${updatedProduct.name}" updated successfully!`, 'success');
    
    console.log('✅ Product updated successfully:', updatedProduct);
    
    // Start loading indicator
    setIsUpdatingProducts(true);
    
    // Wait 5 seconds for Shopify to process the update, then refresh
    setTimeout(async () => {
      console.log('🔄 Fetching updated data from Shopify after 5 second delay...');
      await fetchProducts();
      setIsUpdatingProducts(false); // Hide loading indicator
    }, 5000);
  };

  // Handle product deletion
  const handleDeleteProduct = async () => {
    if (!deletingProduct) return;

    try {
      console.log('🗑️ Deleting product:', deletingProduct.name);
      
      const response = await fetch('/.netlify/functions/shopify-delete-product', adminFetchInit({
        method: 'POST',
        body: JSON.stringify({ productId: deletingProduct.shopifyId || deletingProduct.id })
      }));

      const result = await response.json();

      if (result.success) {
        // Remove the product from the products list
        setProducts(prev => 
          prev.filter(product => 
            product.shopifyId !== deletingProduct.shopifyId && 
            product.id !== deletingProduct.id
          )
        );
        
        // Show success toast
        showToastMessage(`Product "${deletingProduct.name}" deleted successfully!`, 'success');
        
        console.log('✅ Product deleted successfully');
        
        // Close delete confirmation
        closeDeleteConfirm();
      } else {
        throw new Error(result.error || 'Failed to delete product');
      }
    } catch (error) {
      console.error('❌ Error deleting product:', error);
      showToastMessage(`Failed to delete product: ${error.message}`, 'error');
    }
  };



  // Redirect if not authenticated (but don't show loading screen)
  if (!isLoading && !isAuthenticated) {
    return null; // Will redirect in useEffect
  }

  return (
    <div className="h-screen bg-gray-900 flex">
      {/* Sidebar - Using reusable component */}
      <Sidebar 
        activeSidebarItem="products"
        isMobileMenuOpen={isMobileMenuOpen}
        onCloseMobileMenu={() => setIsMobileMenuOpen(false)}
        dashboardType="admin"
      />

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={toggleMobileMenu}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col h-screen lg:ml-0">
        {/* Top Bar */}
        <div className=" px-4 py-4 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {/* Mobile Menu Button */}
              <button
                onClick={toggleMobileMenu}
                className="lg:hidden p-2 rounded-md text-gray-400 hover:text-white hover:bg-gray-700"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              
              <div>
                <h1 className="text-xl font-semibold text-white">Products</h1>
                <p className="text-sm text-gray-400">Manage your store inventory</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <button 
                onClick={openAddProductModal}
                className="bg-pink-600 hover:bg-pink-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
              >
                + Add Product
              </button>
            </div>
          </div>
        </div>

        {/* Content Area - Scrollable */}
        <div className="flex-1 overflow-y-auto p-6 bg-gray-900 sidebar-scroll">

                    {/* Filters and Search */}
          <div className="bg-gray-800 rounded-lg shadow p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
              {/* Search */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Search Products
                </label>
                <input
                  type="text"
                  placeholder="Search by name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500 text-white bg-gray-700 placeholder-gray-400"
                />
              </div>

              {/* Category Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Category
                </label>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500 text-white bg-gray-700"
                >
                  <option value="all" className="text-white bg-gray-700">All Categories</option>
                  {categories.map(category => (
                    <option key={category} value={category} className="text-white bg-gray-700">{category}</option>
                  ))}
                </select>
              </div>

              {/* Status Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Status
                </label>
                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500 text-white bg-gray-700"
                >
                  <option value="all" className="text-white bg-gray-700">All Status</option>
                  <option value="active" className="text-white bg-gray-700">Active</option>
                  <option value="inactive" className="text-white bg-gray-700">Inactive</option>
                </select>
              </div>

              {/* Vendor Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Vendor
                </label>
                <select
                  value={selectedVendor}
                  onChange={(e) => setSelectedVendor(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500 text-white bg-gray-700"
                >
                  <option value="all" className="text-white bg-gray-700">All Vendors</option>
                  {vendors.map(vendor => (
                    <option key={vendor} value={vendor} className="text-white bg-gray-700">{vendor}</option>
                  ))}
                </select>
              </div>

              {/* View Mode Toggle */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  View Mode
                </label>
                <div className="flex rounded-md shadow-sm">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`px-4 py-2 text-sm font-medium rounded-l-md border transition-colors ${
                      viewMode === 'grid'
                        ? 'bg-pink-600 text-white border-pink-600'
                        : 'bg-gray-700 text-gray-300 border-gray-600 hover:bg-gray-600 hover:text-white'
                    }`}
                  >
                    Grid
                  </button>
                  <button
                    onClick={() => setViewMode('table')}
                    className={`px-4 py-2 text-sm font-medium rounded-r-md border-t border-r border-b transition-colors ${
                      viewMode === 'table'
                        ? 'bg-pink-600 text-white border-pink-600'
                        : 'bg-gray-700 text-gray-300 border-gray-600 hover:bg-gray-600 hover:text-white'
                    }`}
                  >
                    Table
                  </button>
                </div>
              </div>
            </div>

            {/* Results Count */}
            <div className="flex justify-between items-center">
              <div className="text-sm text-gray-300">
                Showing {filteredProducts.length} of {products.length} products
              </div>
              {isFallbackData && (
                <div className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-xs font-medium">
                  ⚠️ Using sample data - Shopify not configured
                </div>
              )}
              {productsError && (
                <div className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-xs font-medium">
                  ❌ API Error: {productsError}
                </div>
              )}
            </div>
          </div>

          {/* Products Display */}
          {loadingProducts ? (
            <div className="bg-gray-800 rounded-2xl border border-gray-700 p-16 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500 mx-auto mb-4"></div>
              <h3 className="text-lg font-medium text-white mb-2">Loading Products...</h3>
              <p className="text-gray-400">Please wait while we fetch your products from Shopify</p>
            </div>
          ) : isUpdatingProducts ? (
            <div className="bg-gray-800 rounded-2xl border border-gray-700 p-16 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
              <h3 className="text-lg font-medium text-white mb-2">Syncing with Shopify...</h3>
              <p className="text-gray-400">Updating product data, please wait</p>
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="bg-gray-800 rounded-2xl border border-gray-700 p-16 text-center">
              <div className="max-w-md mx-auto">
                <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-pink-900/20 to-purple-900/20 rounded-full flex items-center justify-center">
                  <svg className="w-12 h-12 text-pink-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-white mb-2">
                  {products.length === 0 ? 'No products yet' : 'No matching products'}
                </h3>
                <p className="text-gray-400 mb-6 leading-relaxed">
                  {products.length === 0 
                    ? 'Get started by creating your first product using the Shopify API.'
                    : 'Try adjusting your search terms or filters to find what you\'re looking for.'
                  }
                </p>
                {productsError && (
                  <div className="text-sm text-gray-500 bg-gray-800 border border-gray-600 rounded-lg p-4">
                    <p className="font-medium mb-2">🔧 API Error Details:</p>
                    <p className="text-red-400">{productsError}</p>
                  </div>
                )}
              </div>
            </div>
          ) : viewMode === 'grid' ? (
            /* Grid View - Enhanced Professional Design */
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-8">
              {filteredProducts.map(product => (
                <div key={product.shopifyId} className="group bg-gray-800 rounded-2xl shadow-sm hover:shadow-2xl transition-all duration-300 border border-gray-700 hover:border-gray-600 overflow-hidden">
                  {/* Product Image Section */}
                  <div className="relative overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100">
                    <div className="aspect-w-4 aspect-h-3 relative">
                      <img
                        src={product.image}
                        alt={product.name}
                        className="w-full h-56 object-cover object-center group-hover:scale-105 transition-transform duration-500"
                        onError={(e) => {
                          // Prevent infinite loop by checking if we already set a fallback
                          if (!e.target.dataset.fallback) {
                            e.target.dataset.fallback = 'true';
                            // Use a data URL instead of external placeholder to avoid network requests
                            e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KICA8cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjNmNGY2Ii8+CiAgPHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxOCIgZmlsbD0iIzljYTNhZiIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPk5vIEltYWdlPC90ZXh0Pgo8L3N2Zz4K';
                          }
                        }}
                      />
                      
                      {/* Image overlay gradient */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      
                      {/* Multiple images indicator */}
                      {product.images && product.images.length > 1 && (
                        <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm text-gray-700 text-xs font-semibold px-3 py-1.5 rounded-full shadow-lg">
                          📸 +{product.images.length - 1}
                        </div>
                      )}
                      
                      {/* Status badge */}
                      <div className="absolute top-3 left-3">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold shadow-sm ${
                          product.status === 'active' 
                            ? 'bg-emerald-100 text-emerald-800 border border-emerald-200' 
                            : product.status === 'draft'
                            ? 'bg-amber-100 text-amber-800 border border-amber-200'
                            : 'bg-red-100 text-red-800 border border-red-200'
                        }`}>
                          <div className={`w-2 h-2 rounded-full mr-1.5 ${
                            product.status === 'active' ? 'bg-emerald-400' : 
                            product.status === 'draft' ? 'bg-amber-400' : 'bg-red-400'
                          }`}></div>
                          {product.status.charAt(0).toUpperCase() + product.status.slice(1)}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Product Content */}
                  <div className="p-6">
                    {/* Product Title */}
                    <div className="mb-4">
                      <h3 className="text-lg font-bold text-white mb-1 line-clamp-2 group-hover:text-pink-400 transition-colors">
                        {product.name}
                      </h3>
                      <p className="text-sm text-gray-400 font-medium">
                        {product.category} {product.vendor && `• by ${product.vendor}`}
                      </p>
                    </div>
                    
                    {/* Price Section */}
                    <div className="mb-4">
                      <div className="flex items-baseline justify-between">
                        <span className="text-2xl font-bold text-white">
                          ${typeof product.price === 'string' ? product.price : product.price.toFixed(2)}
                        </span>
                        {product.variants && product.variants.length > 1 && (
                          <span className="text-sm text-pink-400 font-medium bg-pink-900/20 px-2 py-1 rounded-lg">
                            {product.variants.length} variants
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {/* Product Stats */}
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="bg-gray-700 rounded-lg p-3">
                        <div className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">
                          {product.inventoryTracked ? 'Stock' : 'Inventory'}
                        </div>
                        <div className="text-lg font-bold text-white">
                          {product.inventoryTracked 
                            ? product.stock
                            : 'Not tracked'
                          }
                        </div>
                      </div>
                      <div className="bg-gray-700 rounded-lg p-3">
                        <div className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">SKU</div>
                        <div className="text-sm font-mono text-gray-300 truncate">
                          {product.variants?.[0]?.sku || product.shopifyId?.toString().slice(-6) || 'N/A'}
                        </div>
                      </div>
                    </div>
                    
                    {/* Tags */}
                    {product.tags && (
                      <div className="mb-4">
                        <div className="flex flex-wrap gap-2">
                          {product.tags.split(', ').slice(0, 3).map((tag, index) => (
                            <span key={index} className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-gray-700 text-gray-300 hover:bg-gray-600 transition-colors">
                              #{tag.trim()}
                            </span>
                          ))}
                          {product.tags.split(', ').length > 3 && (
                            <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-pink-900/20 text-pink-400">
                              +{product.tags.split(', ').length - 3} more
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* Description preview */}
                    {product.description && (
                      <div className="mb-5">
                        <p className="text-sm text-gray-400 line-clamp-2 leading-relaxed">
                          {product.description.replace(/<[^>]*>/g, '').substring(0, 120)}
                          {product.description.length > 120 && '...'}
                        </p>
                      </div>
                    )}
                    
                    {/* Action Buttons */}
                    <div className="flex gap-3">
                      <button 
                        onClick={() => openEditProductModal(product)}
                        className="flex-1 inline-flex items-center justify-center px-4 py-2.5 bg-pink-600 hover:bg-pink-700 text-white text-sm font-semibold rounded-lg transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        Edit
                      </button>
                      <button 
                        onClick={() => openDeleteConfirm(product)}
                        className="inline-flex items-center justify-center px-4 py-2.5 bg-gray-700 hover:bg-red-600 text-red-400 hover:text-white text-sm font-semibold rounded-lg border-2 border-red-600 hover:border-red-600 transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            /* Table View */
            <div className="bg-gray-800 shadow rounded-lg overflow-hidden border border-gray-700">
              <table className="min-w-full divide-y divide-gray-700">
                <thead className="bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Product
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Details
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Vendor
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Price
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Inventory
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-gray-800 divide-y divide-gray-700">
                  {filteredProducts.map(product => (
                    <tr key={product.shopifyId} className="hover:bg-gray-700">
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className="relative">
                            <img
                              src={product.image}
                              alt={product.name}
                              className="h-12 w-12 rounded-lg object-cover"
                              onError={(e) => {
                                // Prevent infinite loop by checking if we already set a fallback
                                if (!e.target.dataset.fallback) {
                                  e.target.dataset.fallback = 'true';
                                  // Use a smaller data URL for table view
                                  e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDgiIGhlaWdodD0iNDgiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CiAgPHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0iI2YzZjRmNiIvPgogIDx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwsIHNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMTAiIGZpbGw9IiM5Y2EzYWYiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5ObyBJbWFnZTwvdGV4dD4KICA8L3N2Zz4K';
                                }
                              }}
                            />
                            {product.images && product.images.length > 1 && (
                              <div className="absolute -top-1 -right-1 bg-pink-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                                {product.images.length}
                              </div>
                            )}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-white max-w-xs truncate">
                              {product.name}
                            </div>
                            <div className="text-sm text-gray-400">
                              ID: {product.shopifyId?.toString().slice(-8)}
                            </div>
                            {product.tags && (
                              <div className="flex flex-wrap gap-1 mt-1">
                                {product.tags.split(', ').slice(0, 2).map((tag, index) => (
                                  <span key={index} className="bg-gray-700 text-gray-300 text-xs px-1.5 py-0.5 rounded">
                                    {tag}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-white">
                          <div className="font-medium">{product.category}</div>
                          {product.variants && product.variants.length > 1 && (
                            <div className="text-pink-400 text-xs mt-1">
                              {product.variants.length} variants
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-white font-medium">
                          {product.vendor || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-white">
                          ${typeof product.price === 'string' ? product.price : product.price.toFixed(2)}
                        </div>
                        {product.variants && product.variants.length > 1 && (
                          <div className="text-xs text-gray-400">
                            {Math.min(...product.variants.map(v => parseFloat(v.price)))} - 
                            ${Math.max(...product.variants.map(v => parseFloat(v.price)))}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-white">
                          {product.inventoryTracked 
                            ? `${product.stock} in stock`
                            : 'Inventory not tracked'
                          }
                        </div>
                        {product.variants && product.variants.length > 1 && (
                          <div className="text-xs text-gray-400">
                            Total: {product.variants.reduce((sum, v) => sum + (v.quantityAvailable || 0), 0)}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          product.status === 'active' 
                            ? 'bg-green-900/50 text-green-400 border border-green-700' 
                            : 'bg-red-900/50 text-red-400 border border-red-700'
                        }`}>
                          {product.status}
                        </span>
                        {product.createdAt && (
                          <div className="text-xs text-gray-400 mt-1">
                            Created: {new Date(product.createdAt).toLocaleDateString()}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                        <button 
                          onClick={() => openEditProductModal(product)}
                          className="text-pink-400 hover:text-pink-300 transition-colors"
                        >
                          Edit
                        </button>
                        <button 
                          onClick={() => openDeleteConfirm(product)}
                          className="text-red-400 hover:text-red-300 transition-colors"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

        </div>
      </div>

      {/* Add Product Modal */}
      <AddProductModal
        isOpen={showAddProductModal}
        onClose={closeAddProductModal}
        onProductAdded={handleProductAdded}
        enableVendorEdit={true}
      />

      {/* Edit Product Modal */}
      <EditProductModal
        isOpen={showEditProductModal}
        onClose={closeEditProductModal}
        onProductUpdated={handleProductUpdated}
        product={editingProduct}
        enableVendorEdit={true}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && deletingProduct && (
        <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
          <div className="bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 border border-gray-700">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0">
                  <svg className="w-10 h-10 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-white">Delete Product</h3>
                  <p className="text-sm text-gray-400">
                    Are you sure you want to delete "{deletingProduct.name}"? This action cannot be undone.
                  </p>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={closeDeleteConfirm}
                  className="px-4 py-2 border border-gray-600 rounded-md text-gray-300 bg-gray-700 hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteProduct}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {showToast && (
        <div className={`fixed bottom-4 right-4 z-[100] transform transition-all duration-500 ease-in-out ${
          showToast ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
        }`}>
          <div className={`flex items-center p-4 rounded-lg shadow-lg min-w-[300px] max-w-md ${
            toastType === 'success' 
              ? 'bg-green-600 text-white' 
              : 'bg-red-600 text-white'
          }`}>
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                {toastType === 'success' ? (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                )}
              </div>
              <div className="flex-1">
                <p className="font-medium">{toastMessage}</p>
              </div>
              <button 
                onClick={() => setShowToast(false)}
                className="flex-shrink-0 text-white hover:text-gray-200 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminStore; 